import { servicePrintStyle } from "./servicePrintStyle";
import { GDPR_TEXT } from "./ServiceOrderFields";
import ServiceReceptionDocument from "./ServiceReceptionDocument";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner, Alert } from "react-bootstrap";
import { authFetch } from "../utils/authFetch";
import { calculateDevizTotals } from "./devizTotals";

const fmt2 = (v) => {
  if (v === null || v === undefined || v === "") return "0.00";
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

export default function DevizPrint() {
  const { id } = useParams();
  const isCommand = new URLSearchParams(window.location.search).get("document") === "comanda";
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [order, setOrder] = useState(null);
  const [firma, setFirma] = useState(null);
  const [client, setClient] = useState(null);
  const [totals, setTotals] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let printTimer;

    (async () => {
      setBusy(true);
      setErr("");
      try {
        // 1. Fetch internal order
        const ord = await authFetch(`/comenzi/interna/${id}`);
        if (cancelled) return;
        setOrder(ord);

        // A fiscal calculation requires the actual firm and product-group rates.
        const response = await authFetch("/firma");
        const f = response?.items?.[0];
        if (!f) throw new Error("Datele firmei nu au putut fi încărcate.");
        const partIds = new Set((ord.reteta || []).map(row => Number(row.bxpart_id)));
        if (ord.manopere?.length) partIds.add(Number(ord.bxpart_id));
        const parts = new Map(Number(f.platitor_tva) === 1
          ? await Promise.all([...partIds].map(async partId => [partId, await authFetch(`/bxpart/${partId}`)]))
          : []);
        const calculated = calculateDevizTotals(ord, f, parts);
        if (cancelled) return;
        setFirma(f);
        setTotals(calculated);

        // 3. Fetch client details
        if (ord.bxcustomer_id) {
          try {
            const cl = await authFetch(`/contacte/${ord.bxcustomer_id}`);
            if (!cancelled && cl) {
              setClient(cl);
            }
          } catch (e) {
            console.warn("Could not load customer details:", e);
          }
        }

        if (!cancelled) {
          // Trigger print dialog after DOM layout settles
          printTimer = setTimeout(() => window.print(), 350);
        }
      } catch (e) {
        if (!cancelled) setErr(String(e?.message || e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(printTimer);
    };
  }, [id]);

  if (busy) {
    return (
      <div className="p-4 text-center">
        <Spinner animation="border" variant="primary" className="mb-2" />
        <div>Se încarcă devizul...</div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="p-4">
        <Alert variant="danger">Eroare la încărcarea devizului: {err}</Alert>
      </div>
    );
  }

  if (!order) return null;

  return <DevizDocument order={order} firma={firma} client={client} totals={totals} isCommand={isCommand} />;
}

export function DevizDocument({ order, firma, client, totals, isCommand = false }) {

  // Split lines into labor (manopere) and materials (reteta)
  const details = order.service_details || {};
  const manopere = order.manopere || [];
  const reteta = order.reteta || [];

  const { labor: subtotalManopera, materials: subtotalReteta,
    subtotal: subtotalGeneral, total: totalGeneral } = totals;

  return (
    <div className={`print-a4 deviz-print${isCommand ? ' service-command' : ''}`}>
      <style>{servicePrintStyle}</style>
      {/* 1. Header Grid */}
      <div className="print-header border-bottom pb-3 mb-3">
        {/* Supplier details (Firma) */}
        <div>
          <h5 className="fw-bold text-uppercase mb-1">{firma?.bxdenumire || "PRESTATOR"}</h5>
          <div className="small text-muted">
            {firma?.bxcodfiscal && <div>C.U.I.: <b>{firma.bxcodfiscal}</b></div>}
            {firma?.bxregcom && <div>Nr. Reg. Com.: <b>{firma.bxregcom}</b></div>}
            {firma?.bxadresa && <div>Adresă: {firma.bxadresa}</div>}
            {firma?.bxoras && <div>Oraș/Județ: {firma.bxoras} {firma.judet_str ? `, ${firma.judet_str}` : ""}</div>}
            {firma?.bxtel && <div>Telefon: {firma.bxtel}</div>}
            {firma?.bxemail && <div>Email: {firma.bxemail}</div>}
            {firma?.nume_banca1 && <div>Bancă: {firma.nume_banca1}</div>}
            {firma?.cont_banca1 && <div>IBAN: <b>{firma.cont_banca1}</b></div>}
          </div>
        </div>

        {/* Title and Doc info */}
        <div className="text-end">
          <div className="print-title text-primary text-uppercase">{isCommand ? "Comandă de reparație" : "Deviz Estimativ"}</div>
          <div className="print-title fs-5 mb-2">Reparație / Lucrări</div>
          <div className="text-muted small">
            <div>Comandă Nr: <span className="fw-bold text-dark fs-6">{order.bxnumar_comanda || "-"}</span></div>
            <div>Data deschiderii: <span className="fw-bold text-dark">{String(order.bxdata_comanda || "").slice(0, 10) || "-"}</span></div>
            {isCommand && <div>Stare comandă / deviz: <b>{order.bxdatafin ? `Închisă la ${String(order.bxdatafin).slice(0, 10)}` : 'Deschisă'}</b></div>}
            {order.data_livrare && (
              <div>Data finalizării: <span className="fw-bold text-dark">{String(order.data_livrare).slice(0, 10)}</span></div>
            )}
          </div>
        </div>
      </div>

      {/* 2. Client & Auto Information */}
      <div className="row g-3 mb-4">
        {/* Customer box */}
        <div className="col-6">
          <div className="print-box h-100">
            <div className="print-box-title border-bottom pb-1 mb-2 text-primary">BENEFICIAR (CLIENT)</div>
            <div className="small">
              <div className="fw-bold fs-6 mb-1">{client?.bxdenumire || order.customer_name || "-"}</div>
              {client?.bxcodfiscal && <div>Cod Fiscal: <b>{client.bxcodfiscal}</b></div>}
              {client?.bxregcom && <div>Nr. Reg. Com: <b>{client.bxregcom}</b></div>}
              {client?.bxadresa && <div>Adresă: {client.bxadresa}</div>}
              {client?.bxoras && <div>Oraș/Județ: {client.bxoras} {client.judet_str ? `, ${client.judet_str}` : ""}</div>}
              {client?.bxtel && <div>Telefon: {client.bxtel}</div>}
              {client?.bxemail && <div>Email: {client.bxemail}</div>}
            </div>
          </div>
        </div>

        {/* Auto/Vehicle box */}
        <div className="col-6">
          <div className="print-box h-100">
            <div className="print-box-title border-bottom pb-1 mb-2 text-primary">DATE AUTOVEHICUL</div>
            <div className="small">
              <div className="row g-2">
                <div className="col-5 text-muted">Nr. Înmatriculare:</div>
                <div className="col-7 fw-bold">{order.bxnumar || "-"}</div>

                <div className="col-5 text-muted">Serie șasiu (VIN):</div>
                <div className="col-7 fw-bold text-uppercase">{order.bxsasiu || "-"}</div>

                <div className="col-5 text-muted">Rulaj (KM):</div>
                <div className="col-7 fw-bold">{order.bxkm ? `${order.bxkm} km` : "-"}</div>

                <div className="col-5 text-muted">Dovadă / Act:</div>
                <div className="col-7">{order.bxdovada || "-"}</div>

                <div className="col-5 text-muted">{isCommand ? "Marcă / model:" : "Obiect fabricat:"}</div>
                <div className="col-7 fw-semibold text-truncate">{(isCommand ? details.marca_model : order.part_name) || "-"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isCommand && <>
        <div className="grid-2 mb-3">
          <div className="print-box"><b>Solicitarea clientului</b><div className="small muted">Simptome și lucrări cerute</div><div style={{ whiteSpace: 'pre-wrap', minHeight: '24mm' }}>{details.solicitare || order.bxdescr || '................................................................'}</div></div>
          <div className="print-box"><b>Constatarea inițială</b><div className="small muted">Defecțiuni constatate și intervenții propuse</div><div style={{ whiteSpace: 'pre-wrap', minHeight: '24mm' }}>{details.constatare || '................................................................'}</div></div>
        </div>
        <div className="print-box"><b>Starea la primire / combustibil / bunuri și documente predate</b><div style={{ whiteSpace: 'pre-wrap', minHeight: '10mm' }}>{details.primire || '................................................................'}</div></div>
        <p><b>Termen estimat:</b> {details.termen || '................................'}</p>
        <p><b>Recepționer:</b> {details.receptioner || '................................'}<br />
          <b>An fabricație:</b> {details.an_fabricatie || '—'} · <b>Motorizare:</b> {details.motorizare || '—'} · <b>Combustibil:</b> {details.combustibil || 'Neconsemnat'}</p>
        <div className="grid-2 mb-3">
          <div className="print-box"><b>Daune vizibile la recepție</b><div style={{ whiteSpace: 'pre-wrap', minHeight: '12mm' }}>{details.daune || '................................................................'}</div></div>
          <div className="print-box"><b>Obiecte lăsate în mașină</b><div style={{ whiteSpace: 'pre-wrap', minHeight: '12mm' }}>{details.obiecte || '................................................................'}</div></div>
        </div>
        <p><b>Returnarea pieselor înlocuite:</b> {details.returnare_piese === 'da' ? '☑ DA  ☐ NU' : details.returnare_piese === 'nu' ? '☐ DA  ☑ NU' : '☐ DA  ☐ NU'}</p>
      </>}
      {/* 3. Description / Work Request */}
      {!isCommand && order.bxdescr && (
        <div className="print-box mb-4">
          <div className="print-box-title border-bottom pb-1 mb-1 text-primary">CERINȚE BENEFICIAR / DEFECTE SEMNALATE</div>
          <div className="small py-1" style={{ whiteSpace: "pre-wrap" }}>
            {order.bxdescr}
          </div>
        </div>
      )}

      {/* 4. Table 1: Manopera (Labor / Services) */}
      <div className="mb-4">
        <h6 className="fw-bold text-primary border-bottom pb-1 mb-2">I. MANOPERĂ (SERVICII REPARAȚIE)</h6>
        {manopere.length > 0 ? (
          <table className="print-table small">
            <thead>
              <tr className="table-light">
                <th style={{ width: "5%" }}>#</th>
                <th style={{ width: "15%" }}>Cod</th>
                <th>Denumire Operațiune / Manoperă</th>
                <th style={{ width: "12%" }} className="text-end">Cantitate (Ore)</th>
                <th style={{ width: "15%" }} className="text-end">Tarif / Oră</th>
                <th style={{ width: "15%" }} className="text-end">Valoare (RON)</th>
              </tr>
            </thead>
            <tbody>
              {manopere.map((m, idx) => {
                const qty = Number(m.bxqty_manopera) || 0;
                const price = Number(m.bxpret_manopera) || 0;
                const val = qty * price;
                return (
                  <tr key={m.bxid || idx}>
                    <td className="text-muted">{idx + 1}</td>
                    <td><b>{m.bxmanoperacod || "-"}</b></td>
                    <td>{m.bxspecificatii || "Manoperă / Serviciu reparație"}</td>
                    <td className="text-end">{qty.toFixed(2)}</td>
                    <td className="text-end">{fmt2(price)}</td>
                    <td className="text-end fw-bold">{fmt2(val)}</td>
                  </tr>
                );
              })}
              <tr className="table-light fw-bold">
                <td colSpan={5} className="text-end">Subtotal Manoperă (fără TVA):</td>
                <td className="text-end text-primary">{fmt2(subtotalManopera)}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div className="text-muted small py-2 italic border-bottom">Nu există operațiuni de manoperă înregistrate pe această comandă.</div>
        )}
      </div>

      {/* 5. Table 2: Piese si Materiale */}
      <div className="mb-4">
        <h6 className="fw-bold text-primary border-bottom pb-1 mb-2">II. PIESE DE SCHIMB ȘI MATERIALE</h6>
        {reteta.length > 0 ? (
          <table className="print-table small">
            <thead>
              <tr className="table-light">
                <th style={{ width: "5%" }}>#</th>
                <th style={{ width: "15%" }}>Cod</th>
                <th>Denumire Produs / Material</th>
                <th style={{ width: "8%" }}>U.M.</th>
                <th style={{ width: "12%" }} className="text-end">Cantitate</th>
                <th style={{ width: "15%" }} className="text-end">Preț unitar</th>
                <th style={{ width: "15%" }} className="text-end">Valoare (RON)</th>
              </tr>
            </thead>
            <tbody>
              {reteta.map((r, idx) => {
                const qty = Number(r.luat) > 0 ? Number(r.luat) : (Number(r.deluat) > 0 ? Number(r.deluat) : Number(r.bxnorma) || 0);
                const price = Number(r.pretvanzare) || 0;
                const val = qty * price;
                return (
                  <tr key={r.bxid || idx}>
                    <td className="text-muted">{idx + 1}</td>
                    <td><b>{r.part_code || "-"}</b></td>
                    <td>{r.part_name || "-"}</td>
                    <td>{r.um_name || "BUC"}</td>
                    <td className="text-end">{qty.toFixed(2)}</td>
                    <td className="text-end">{fmt2(price)}</td>
                    <td className="text-end fw-bold">{fmt2(val)}</td>
                  </tr>
                );
              })}
              <tr className="table-light fw-bold">
                <td colSpan={6} className="text-end">Subtotal Piese și Materiale (fără TVA):</td>
                <td className="text-end text-primary">{fmt2(subtotalReteta)}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div className="text-muted small py-2 italic border-bottom">Nu există piese de schimb sau materiale înregistrate pe această comandă.</div>
        )}
      </div>

      {/* 6. General Totals */}
      <div className="print-totals mb-5">
        <div className="small muted text-justify pe-4">
          <div className="fw-bold mb-1">Mențiuni și Condiții:</div>
          <div>Prezentul document reprezintă un deviz de lucrări estimativ. Valorile finale pot varia în funcție de lucrările suplimentare neprevăzute apărute în timpul reparației, agreate în prealabil cu beneficiarul. Garanția pieselor de schimb și a manoperei este acordată conform legislației în vigoare.</div>
        </div>

        <div className="totals-box bg-light">
          <div className="row2 small">
            <div className="muted">Total Manoperă:</div>
            <div className="text-end">{fmt2(subtotalManopera)} RON</div>
          </div>
          <div className="row2 small">
            <div className="muted">Total Piese/Materiale:</div>
            <div className="text-end">{fmt2(subtotalReteta)} RON</div>
          </div>
          <div className="row2 small border-top pt-2">
            <div className="muted">Subtotal (fără TVA):</div>
            <div className="text-end fw-semibold">{fmt2(subtotalGeneral)} RON</div>
          </div>
          <div className="row2 small">
            {totals.vatRows.map(row => <React.Fragment key={row.rate}>
              <div className="muted">{Number(firma.platitor_tva) === 0 ? "TVA (neplătitor):" : `TVA (${row.rate}%):`}</div>
              <div className="text-end">{fmt2(row.vat)} RON</div>
            </React.Fragment>)}
          </div>
          <div className="row2 grand text-primary border-top pt-2">
            <div>TOTAL DEVIZ:</div>
            <div className="text-end">{fmt2(totalGeneral)} RON</div>
          </div>
        </div>
      </div>

      {isCommand && <div className="print-box" style={{ breakInside: 'avoid' }}>
        <b>Acordul clientului pentru comanda inițială</b>
        <p>Autorizez lucrările și piesele înscrise în prezenta comandă, la valoarea estimată de {fmt2(totalGeneral)} RON, conform termenului indicat. Lucrările suplimentare se consemnează într-o anexă separată și se execută numai după acordul meu scris.</p>
        <p>{details.probe_drum ? '☑' : '☐'} Sunt de acord cu efectuarea probelor de drum de către personalul service-ului.</p>
        <p>Client / reprezentant: {details.reprezentant || '................................'}<br />Data și ora: ................................ Semnătura: ................................</p>
        <hr /><b>Informare GDPR</b>
        <p>{details.gdpr_informat ? '☑' : '☐'} {GDPR_TEXT}</p>
        <p className="small">Confirmarea privește informarea oferită de service și nu include consimțământ pentru marketing.</p>
        <p>Data: ................................ Semnătura clientului: ................................</p>
      </div>}
      {/* 7. Signatures footer */}
      <div className="print-footer border-top pt-3 small muted mt-5">
        <div className="row text-center mt-3">
          <div className="col-4">
            <div><b>PRESTATOR (SERVICE AUTO)</b></div>
            <div className="mt-5 pt-3 border-top mx-auto" style={{ maxWidth: "180px" }}>Semnătură / Ștampilă</div>
          </div>
          <div className="col-4">
            <div><b>INTOCMIT DE</b></div>
            <div className="mt-5 pt-3 border-top mx-auto" style={{ maxWidth: "180px" }}>Semnătură</div>
          </div>
          <div className="col-4">
            <div><b>BENEFICIAR (CLIENT)</b></div>
            <div className="mt-5 pt-3 border-top mx-auto" style={{ maxWidth: "180px" }}>Semnătură de primire</div>
          </div>
        </div>
      </div>

      {/* Print Button (Screen only) */}
      {isCommand && <ServiceReceptionDocument order={order} firma={firma} client={client} />}
      <div className="no-print mt-4 border-top pt-3 text-center">
        <button className="btn btn-primary px-4 fw-bold shadow-sm" onClick={() => window.print()}>
          {isCommand ? "Print comandă" : "Printează Devizul"}
        </button>
      </div>
    </div>
  );
}
